import React, { useState, useEffect, useRef } from "react";
import { Html5QrcodeScanner } from "html5-qrcode";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import { baseApiURL } from "../../../baseUrl";
import { FaQrcode, FaCheckCircle, FaExclamationTriangle, FaUserGraduate, FaSignOutAlt, FaSignInAlt, FaHistory } from "react-icons/fa";

const HostelGateScanner = () => {
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState("checkout"); // 'checkout' or 'checkin'
  const [manualPassNo, setManualPassNo] = useState("");
  const [scanHistory, setScanHistory] = useState([]);
  const scannerRef = useRef(null);

  useEffect(() => {
    const scanner = new Html5QrcodeScanner(
      "hostel-qr-reader",
      {
        qrbox: { width: 240, height: 240 },
        fps: 8,
      },
      false
    );

    scannerRef.current = scanner;

    const onScanSuccess = async (decodedText) => {
      // Format might be GP-XXXXXX or JSON
      let passNumber = decodedText;
      try {
        const parsed = JSON.parse(decodedText);
        if (parsed.passNumber) passNumber = parsed.passNumber;
      } catch (e) {
        // Raw string
      }
      processScan(passNumber);
    };

    scanner.render(onScanSuccess, (err) => {
      // scanning
    });

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
      }
    };
  }, [actionType]);

  const processScan = async (passNumber) => {
    if (!passNumber) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${baseApiURL()}/hostel/gatepass/scan`,
        {
          passNumber,
          action: actionType,
          guardName: "Main Gate Incharge",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success(res.data.message);
        setScanResult(res.data.pass);
        setScanHistory((prev) => [
          {
            passNumber,
            student: res.data.pass?.studentId,
            action: actionType,
            time: new Date().toLocaleTimeString(),
            isOverdue: res.data.pass?.isOverdue,
          },
          ...prev.slice(0, 9),
        ]);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Scan failed");
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    processScan(manualPassNo);
    setManualPassNo("");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <FaQrcode className="text-rose-500" /> Security Gate QR Terminal
          </h2>
          <p className="text-xs text-slate-500">
            Real-time Digital Gate Pass verification & check-out / check-in logging
          </p>
        </div>

        {/* Action Toggle */}
        <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
          <button
            onClick={() => setActionType("checkout")}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition cursor-pointer ${
              actionType === "checkout"
                ? "bg-amber-500 text-white shadow-xs"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
            }`}
          >
            <FaSignOutAlt /> Student Check-OUT
          </button>
          <button
            onClick={() => setActionType("checkin")}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition cursor-pointer ${
              actionType === "checkin"
                ? "bg-emerald-600 text-white shadow-xs"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
            }`}
          >
            <FaSignInAlt /> Student Check-IN
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Camera Scanner Box */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <div className="text-center font-bold text-xs uppercase tracking-wider text-slate-500">
            Align Student QR Pass in Scanner
          </div>

          <div id="hostel-qr-reader" className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700" />

          {/* Manual Entry Fallback */}
          <form onSubmit={handleManualSubmit} className="pt-3 border-t border-slate-100 dark:border-slate-800 flex gap-2">
            <input
              type="text"
              placeholder="Or enter Gate Pass Number (e.g. GP-123456)"
              value={manualPassNo}
              onChange={(e) => setManualPassNo(e.target.value)}
              className="flex-1 px-3.5 py-2 text-xs rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700 font-mono"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-xs font-bold rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 cursor-pointer"
            >
              Verify Pass
            </button>
          </form>
        </div>

        {/* Scan Result & Details Panel */}
        <div className="space-y-4">
          {scanResult ? (
            <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border-2 border-emerald-500 shadow-md space-y-3">
              <div className="flex justify-between items-center text-emerald-600 font-bold text-xs">
                <span className="flex items-center gap-1">
                  <FaCheckCircle /> Pass Validated
                </span>
                <span className="font-mono">{scanResult.passNumber}</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-slate-200 dark:bg-slate-700 flex items-center justify-center font-bold text-slate-700 text-lg">
                  {scanResult.studentId?.firstName?.[0]}
                </div>
                <div>
                  <h4 className="font-black text-sm text-slate-900 dark:text-slate-100">
                    {scanResult.studentId?.firstName} {scanResult.studentId?.lastName}
                  </h4>
                  <p className="text-xs text-slate-500 font-mono">
                    {scanResult.studentId?.enrollmentNo}
                  </p>
                </div>
              </div>

              <div className="space-y-1 text-xs text-slate-600 dark:text-slate-300 pt-2 border-t border-slate-100 dark:border-slate-800">
                <div><span className="font-bold">Pass Type:</span> {scanResult.passType}</div>
                <div><span className="font-bold">Destination:</span> {scanResult.destination}</div>
                <div><span className="font-bold">Hostel Block:</span> {scanResult.blockId?.blockName}</div>
                <div><span className="font-bold">Parent Phone:</span> {scanResult.studentId?.fatherPhoneNumber || "N/A"}</div>
              </div>

              {scanResult.isOverdue && (
                <div className="p-2.5 rounded-xl bg-rose-100 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 text-xs font-bold flex items-center gap-1.5">
                  <FaExclamationTriangle /> LATE / OVERDUE ENTRY RECORDED
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm text-center py-10 space-y-2">
              <FaQrcode className="text-4xl text-slate-300 dark:text-slate-700 mx-auto" />
              <p className="text-xs text-slate-400">
                Scan result will appear here immediately
              </p>
            </div>
          )}

          {/* Recent Gate Scan Ledger */}
          <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-2">
            <h4 className="font-bold text-xs text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
              <FaHistory className="text-slate-400" /> Recent Gate Logs
            </h4>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {scanHistory.map((h, i) => (
                <div key={i} className="flex justify-between items-center text-[11px] p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <div>
                    <span className="font-bold">{h.student?.firstName || "Student"}</span> ({h.passNumber})
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`font-bold uppercase text-[9px] px-1.5 py-0.5 rounded ${h.action === "checkout" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {h.action}
                    </span>
                    <span className="text-slate-400 text-[10px]">{h.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostelGateScanner;
